import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/common/SEO';
import Breadcrumbs from '../../components/common/Breadcrumbs';

const PublicCsvFormat = () => {
  const invoiceUploadData = [
    { header: 'stripe_customer_id', requirement: '〇', description: 'Stripeの顧客IDを文字列で指定します。cus_********で記載されているID。' },
    { header: 'paymentsettings', requirement: '〇', description: '請求時の支払い方法を指定します。クレジットカード（card）か銀行振り込み（bank）を選択できます。cardまたはbankのいずれかである必要があります。半角のみ（大文字・小文字は区別されません）。' },
    { header: 'price_id', requirement: '※', description: 'Stripeの商品価格IDを文字列で指定します。unit_amountが指定されていない場合に必須となります。price_********で記載されているID。' },
    { header: 'quantity', requirement: '〇', description: '請求する数量です。1以上の整数である必要があります。半角数字のみ。' },
    { header: 'description', requirement: '△', description: '請求書に表示される説明（明細）を文字列で指定します。Price_idが指定されている場合、price_idに設定した説明（明細）が優先されてが表示されます。' },
    { header: 'unit_amount', requirement: '※', description: '商品の単価を整数で指定します。price_idが指定されていない場合に必須となります。半角数字のみ。' },
    { header: 'tax', requirement: '〇', description: 'Billwaveに設定した税率IDを指定します。0以上の半角数字のみ。0を指定すると税なしとなります。' },
    { header: 'period_start', requirement: '〇', description: '提供サービス、販売品の利用開始日。YYYY-MM-DDもしくはYYYY/MM/DD形式の日付。半角のみ。' },
    { header: 'period_end', requirement: '〇', description: '提供サービス、販売品の利用終了日。period_startと同じか、それ以降の日付である必要があります。YYYY-MM-DDもしくはYYYY/MM/DD形式の日付。半角のみ。' },
    { header: 'due_date', requirement: '※', description: '請求書の支払い期日。paymentsettingsがbankの場合に必須となります。YYYY-MM-DDもしくはYYYY/MM/DD形式で指定します。半角のみ。' },
    { header: 'issue_at', requirement: '△', description: '請求書の発行日時を本日より30日以内で指定できます。日付はYYYY-MM-DDまたはYYYY/MM/DD。時刻はHH:MM形式で指定します。省略した場合や過去の日時の場合は、処理実行時の日時が適用されます。半角のみ。' },
    { header: 'draft', requirement: '△', description: '請求書を確定させず、下書き（Draft）としてStripeに保存したい場合に 1 を指定します。空欄または1以外の値は即時発行（または予約発行）となります。半角数字のみ。' },
  ];

  const customerUploadData = [
    { header: 'name', requirement: '〇', description: '顧客名（会社名 or 個人名）。1～255文字で設定できます。' },
    { header: 'email', requirement: '〇', description: '顧客のメールアドレス。請求書はこのアドレス宛に送られます。' },
    { header: 'description', requirement: '△', description: '顧客説明の備考です。任意文字列で最大500文字程度推奨となります。' },
    { header: 'phone', requirement: '△', description: '電話番号。+国番号含めることもできます。' },
    { header: 'address_line1', requirement: '△', description: '顧客住所の番地・建物。' },
    { header: 'address_line2', requirement: '△', description: '顧客住所の建物名など。' },
    { header: 'address_city', requirement: '△', description: '顧客住所の市区町村。' },
    { header: 'address_state', requirement: '△', description: '顧客住所の都道府県。' },
    { header: 'address_postal_code', requirement: '△', description: '郵便番号。数字＋ハイフン。' },
    { header: 'address_country', requirement: '△', description: '国コード。ISO 3166-1 alpha-2（JP, USなど）を入力します。英字大文字。' },
    { header: 'metadata_*', requirement: '△', description: 'カスタム属性として任意に設定できます。「*」には任意の英数字を入れてください。複数のメタデータを登録できます。（metadata_category,metadata_1など）' },
  ];

  const customerDeleteData = [
    { header: 'customer_id', requirement: '〇', description: 'Stripeの顧客IDを文字列で指定します。cus_********で記載されているID。' },
  ];

  return (
    <>
      <SEO 
        title="CSVフォーマット仕様書｜Stripe請求書一括作成 | Billwave"
        description="Billwaveで利用する請求書一括作成・顧客登録用CSVファイルのフォーマット仕様書です。Stripe顧客ID、単価、数量、税率などの必須項目や複数明細の作成方法、サンプルコードおよびCSVテンプレートを無料でダウンロードできます。"
        path="/support/csv-format"
      />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Breadcrumbs items={[{ label: 'CSVフォーマット' }]} />

        <div className="bg-white p-8 shadow-lg rounded-lg border border-gray-100">
          <h1 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-gray-200">
            CSVフォーマット仕様書
          </h1>

          {/* Section 1: CSV File Format Download */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. CSVテンプレートのダウンロード</h2>
            <p className="text-gray-600 mb-4">以下のリンクから用途別のテンプレートCSVファイルをダウンロードしてご使用いただけます。</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <a href="/downloads/invoice_csv_format.csv" download className="text-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg transition-colors shadow-sm">
                請求書アップロード用CSV
              </a>
              <a href="/downloads/customer_csv_format.csv" download className="text-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg transition-colors shadow-sm">
                顧客アップロード用CSV
              </a>
              <a href="/downloads/customerdelete_csv_format.csv" download className="text-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg transition-colors shadow-sm">
                顧客データ削除用CSV
              </a>
            </div>
          </section>

          <hr className="my-8 border-gray-200" />

          {/* Section 2: CSV File Item Description */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. CSVファイル項目説明</h2>
            
            {/* Invoice Upload Section */}
            <div className="mb-12">
              <h3 className="text-xl font-semibold text-gray-700 mb-3">請求書アップロード用フォーマット</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-300">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="py-2 px-4 border-b text-left">ヘッダー項目</th>
                      <th className="py-2 px-4 border-b text-center">要件</th>
                      <th className="py-2 px-4 border-b text-left">説明</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceUploadData.map((item, index) => (
                      <tr key={index}>
                        <td className="py-2 px-4 border-b"><code className="bg-gray-200 px-1 rounded text-sm">{item.header}</code></td>
                        <td className="py-2 px-4 border-b text-center">{item.requirement}</td>
                        <td className="py-2 px-4 border-b text-sm text-gray-700">{item.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-sm text-gray-600 mt-2">〇：必須　△：任意　※：条件付き必須</p>
              </div>

              <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 text-blue-800 p-4 rounded-r-md" role="alert">
                <p className="font-bold">ヒント</p>
                <p className="mt-2 text-sm">CSVファイルをアップロードする際、フォーマットで定義されたヘッダー項目以外を追加してもエラーになりません。<br />CSVファイルを作成、アップロードする際はわかりやすい項目（例えば顧客名など）を追加しておくと便利です。</p>
              </div>

              <div className="mt-6">
                <h4 className="text-lg font-semibold text-gray-700 mb-2">サンプルデータ</h4>
                <div className="mb-4">
                  <h5 className="font-semibold text-gray-600 mb-1 text-sm">・Stripeに登録しているprice_idを利用、請求日時を指定する場合</h5>
                  <pre className="bg-gray-100 p-4 rounded-lg text-xs md:text-sm overflow-x-auto text-gray-800"><code>
{`stripe_customer_id,paymentsettings,price_id,quantity,description,unit_amount,tax,period_start,period_end,due_date,issue_at,draft
cus_**********,card,price_**************,3,,,1,2026/4/1,2026/4/30,2026/6/30,2026/5/31 9:00,`}
                  </code></pre>
                </div>
                <div className="mb-4">
                  <h5 className="font-semibold text-gray-600 mb-1 text-sm">・商品の明細と単価をCSVファイルで指定、すぐに請求書を発行する場合</h5>
                  <pre className="bg-gray-100 p-4 rounded-lg text-xs md:text-sm overflow-x-auto text-gray-800"><code>
{`stripe_customer_id,paymentsettings,price_id,quantity,description,unit_amount,tax,period_start,period_end,due_date,issue_at,draft
cus_**********,card,,3,シグナルネットワークサービス利用料,1000,1,2026/4/1,2026/4/30,2026/6/30,,`}
                  </code></pre>
                </div>
                <p className="text-xs text-gray-500 mt-2">※cus_**********とprice_**************は実際の値を入力してください</p>
              </div>
            </div>

            {/* Customer Upload Section */}
            <div className="mb-12">
              <h3 className="text-xl font-semibold text-gray-700 mb-3">顧客アップロード用フォーマット</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-300">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="py-2 px-4 border-b text-left">ヘッダー項目</th>
                      <th className="py-2 px-4 border-b text-center">要件</th>
                      <th className="py-2 px-4 border-b text-left">説明</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerUploadData.map((item, index) => (
                      <tr key={index}>
                        <td className="py-2 px-4 border-b"><code className="bg-gray-200 px-1 rounded text-sm">{item.header}</code></td>
                        <td className="py-2 px-4 border-b text-center">{item.requirement}</td>
                        <td className="py-2 px-4 border-b text-sm text-gray-700">{item.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-sm text-gray-600 mt-2">〇：必須　△：任意</p>
              </div>
              
              <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 text-blue-800 p-4 rounded-r-md" role="alert">
                <p className="font-bold">ヒント</p>
                <p className="mt-2 text-sm">ヘッダー項目は順番が入れ替わってもエラーになりません。また使用しない項目は削除してもエラーになりません。フォーマットファイルをダウンロードした後は管理しやすい順番に変更ができます。</p>
              </div>

              <div className="mt-6">
                <h4 className="text-lg font-semibold text-gray-700 mb-2">サンプルデータ</h4>
                <div className="mb-4">
                  <h5 className="font-semibold text-gray-600 mb-1 text-sm">・必須項目のみ</h5>
                  <pre className="bg-gray-100 p-4 rounded-lg text-xs md:text-sm overflow-x-auto text-gray-800"><code>
{`name,email
シグナルテスト,test@signalnetwork.jp`}
                  </code></pre>
                </div>
                <div className="mb-4">
                  <h5 className="font-semibold text-gray-600 mb-1 text-sm">・全項目入力</h5>
                  <pre className="bg-gray-100 p-4 rounded-lg text-xs md:text-sm overflow-x-auto text-gray-800"><code>
{`name,email,description,phone,address_line1,address_line2,address_city,address_state,address_postal_code,address_country,metadata_category,metadata_Purpose
シグナル,test@signalnetwork.jp,シグナルのテスト顧客,03-6426-5805,神田多町2-1,神田Y5ビル9F,千代田区,東京都,101-0046,JP,法人1,Billwaveテスト用顧客データ`}
                  </code></pre>
                </div>
              </div>
            </div>

            {/* Customer Delete Section */}
            <div className="mb-12">
              <h3 className="text-xl font-semibold text-gray-700 mb-3">顧客データ削除用フォーマット</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-300">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="py-2 px-4 border-b text-left">ヘッダー項目</th>
                      <th className="py-2 px-4 border-b text-center">要件</th>
                      <th className="py-2 px-4 border-b text-left">説明</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerDeleteData.map((item, index) => (
                      <tr key={index}>
                        <td className="py-2 px-4 border-b"><code className="bg-gray-200 px-1 rounded text-sm">{item.header}</code></td>
                        <td className="py-2 px-4 border-b text-center">{item.requirement}</td>
                        <td className="py-2 px-4 border-b text-sm text-gray-700">{item.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-sm text-gray-600 mt-2">〇：必須</p>
              </div>
            </div>

          </section>

          <hr className="my-8 border-gray-200" />

          {/* Section 3: Invoicing with Multiple Line Items */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. 複数明細の請求書発行</h2>
            <p className="mb-4 text-gray-700">
              1枚の請求書に複数の明細を発行したい場合は以下の方法でCSVファイルを作成してください。
            </p>
            <p className="mb-4 text-gray-700">
              同じStripe顧客ID（<code className="bg-gray-200 px-1 rounded">stripe_customer_id</code>）を使って明細ごとに行を追加してください。<br />
              その際、すべての行の<code className="bg-gray-200 px-1 rounded">paymentsettings</code>、<code className="bg-gray-200 px-1 rounded">due_date</code>、<code className="bg-gray-200 px-1 rounded">issue_at</code>、および<code className="bg-gray-200 px-1 rounded">draft</code>は同じ値を設定してください。
            </p>

            <div className="mt-6">
              <h4 className="text-lg font-semibold text-gray-700 mb-2">サンプルコード</h4>
              <pre className="bg-gray-100 p-4 rounded-lg text-xs md:text-sm overflow-x-auto text-gray-800"><code>
{`stripe_customer_id,paymentsettings,price_id,quantity,description,unit_amount,tax,period_start,period_end,due_date,issue_at,draft
cus_XXXXXXXXXX1,bank,price_000000000000,1,,,1,2026/3/1,2026/3/1,2026/4/30,2026/4/3 0:00,
cus_XXXXXXXXXX1,bank,,1,Billwave利用料金,1000,1,2026/3/2,2026/4/1,2026/4/30,2026/4/3 0:00,
cus_XXXXXXXXXX1,bank,price_000000000001,5,,,1,2026/3/2,2026/3/31,2026/4/30,2026/4/3 0:00,`}
              </code></pre>
            </div>

            <div className="mt-6">
              <h4 className="text-lg font-semibold text-red-700 mb-2">NG例</h4>
              <pre className="bg-red-50 p-4 rounded-lg text-xs md:text-sm overflow-x-auto text-gray-800 border border-red-200"><code>
{`stripe_customer_id,paymentsettings,price_id,quantity,description,unit_amount,tax,period_start,period_end,due_date,issue_at,draft
cus_XXXXXXXXXX1,card,price_****************,1,,,1,2026/3/1,2026/3/1,2026/4/30,2026/4/3 0:00,
cus_XXXXXXXXXX1,bank,,1,Billwave利用料金,1000,1,2026/3/2,2026/4/1,2026/5/30,,1
cus_XXXXXXXXXX1,bank,,1,Billwave利用料金,1000,1,2026/3/2,2026/4/1,2026/5/30,2026/4/3 0:00,`}
              </code></pre>
              <div className="mt-4 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 p-4 rounded-r-md" role="alert">
                <p className="text-sm">上記の場合、stripe_customer_idが同じでも3通の請求書が発行されます。（paymentsettingsやdraftの値が異なるため、別の請求書として扱われます）</p>
              </div>
            </div>
          </section>

          <hr className="my-10 border-gray-200" />

          {/* Natural Internal Links & CTA */}
          <section className="bg-blue-50 rounded-xl p-6 text-center border border-blue-100">
            <h2 className="text-xl font-bold text-gray-900 mb-3">準備ができたらアップロードを始めましょう</h2>
            <p className="text-sm text-gray-600 mb-6">
              使い方の手順やQ&Aも合わせてご確認ください。
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/support/how-to-use" className="bg-white text-blue-600 border border-blue-600 hover:bg-blue-50 font-bold py-2.5 px-6 rounded-lg transition-colors">
                利用ガイドを見る
              </Link>
              <Link to="/faq" className="bg-white text-gray-700 border border-gray-300 hover:bg-gray-100 font-bold py-2.5 px-6 rounded-lg transition-colors">
                FAQを確認する
              </Link>
              <Link to="/signup" className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-6 rounded-lg transition-colors shadow">
                無料新規登録
              </Link>
            </div>
          </section>

        </div>
      </div>
    </>
  );
};

export default PublicCsvFormat;
